import { IconButton, Stack, Typography, Tooltip, Snackbar } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useEffect, useState } from "react";
import { vote } from "../api/votes";
import { motion } from "framer-motion";

export default function VoteButtons({
  postId,
  score,
  myVote,
  onVoted,
}: {
  postId: number;
  score: number;
  myVote: number; // -1, 0, 1
  onVoted: () => void;
}) {
  const [localScore, setLocalScore] = useState(score);
  const [localMyVote, setLocalMyVote] = useState(myVote);
  const [showError, setShowError] = useState(false);

  // keep in sync when feed refreshes
  useEffect(() => setLocalScore(score), [score]);
  useEffect(() => setLocalMyVote(myVote), [myVote]);

  async function doVote(v: 1 | -1, e: React.MouseEvent) {
    e.stopPropagation();

    const prev = localMyVote; // -1 | 0 | 1
    const next = prev === v ? 0 : v; // toggle off if same

    try {
      await vote(postId, next);

      const delta = next - prev;
      setLocalScore((s) => s + delta);
      setLocalMyVote(next);

      onVoted();
    } catch {
      setShowError(true);
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0} sx={{ bgcolor: 'action.hover', borderRadius: 8, p: 0.5 }}>
      <Tooltip title="Upvote">
        <motion.div whileTap={{ scale: 0.8 }}>
            <IconButton
                onClick={(e) => doVote(1, e)}
                size="small"
                sx={{ 
                    color: localMyVote === 1 ? 'primary.main' : 'text.secondary',
                    p: 1,
                    '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                }}
            >
                <KeyboardArrowUpIcon fontSize="small" sx={{ fontWeight: 700 }} />
            </IconButton>
        </motion.div>
      </Tooltip>

      <Typography variant="body2" fontWeight={700} color={localMyVote !== 0 ? 'primary.main' : 'text.primary'} sx={{ mx: 0.5, minWidth: 24, textAlign: 'center' }}>
        {localScore}
      </Typography>

      <Tooltip title="Downvote">
        <motion.div whileTap={{ scale: 0.8 }}>
            <IconButton
                onClick={(e) => doVote(-1, e)}
                size="small"
                sx={{ 
                    color: localMyVote === -1 ? 'error.main' : 'text.secondary',
                    p: 1,
                    '&:hover': { bgcolor: 'transparent', color: 'error.main' }
                }}
            >
                <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
        </motion.div>
      </Tooltip>

      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        message="Failed to vote. Please try again."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}
